import type { EdgeCondition, FlowDefinition, FlowEdge, FlowNode } from '../schema/flow'
import type { AnswerValue } from '../schema/answers'
import { isAnswered, asStringArray } from '../schema/answers'
import { optionValue } from '../schema/common'
import type { FieldRef } from '../schema/expr'
import { evaluateExpr } from './expr/eval'
import { findNodeByKey, getNodeField, getPrimaryField } from './fields'

/** อ่านคำตอบที่บันทึกไว้: (รหัสขั้นตอน, ชื่อฟิลด์) → ค่า */
export type AnswerLookup = (nodeKey: string, fieldName?: string) => AnswerValue | undefined

/** แปลง optionId เป็นค่าที่บันทึกจริงในคำตอบ โดยอ้างจาก definition ที่กำลังใช้อยู่ */
export function resolveOptionValue(
  def: FlowDefinition,
  nodeKey: string,
  fieldName: string,
  optionId: string,
): string | undefined {
  const node = findNodeByKey(def, nodeKey)
  if (!node) return undefined
  const field = getNodeField(node, fieldName)
  const option = field?.options.find((item) => item.id === optionId)
  return option ? optionValue(option) : undefined
}

function readAnswer(
  def: FlowDefinition,
  answers: AnswerLookup,
  nodeKey: string,
  fieldName?: string,
): AnswerValue | undefined {
  if (fieldName) return answers(nodeKey, fieldName)
  // ไม่ระบุฟิลด์ = ฟิลด์แรกของขั้นตอนนั้น
  const node = findNodeByKey(def, nodeKey)
  const primary = node ? getPrimaryField(node) : undefined
  return answers(nodeKey, primary?.name)
}

function textEquals(a: string, b: string, caseInsensitive: boolean): boolean {
  const left = a.trim()
  const right = b.trim()
  return caseInsensitive ? left.toLowerCase() === right.toLowerCase() : left === right
}

function toNumber(value: AnswerValue | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null
    const num = Number(trimmed)
    return Number.isFinite(num) ? num : null
  }
  return null
}

/**
 * ประเมินเงื่อนไขหนึ่งเส้น
 * `fallback` คืนค่า true เสมอ แต่ถูกจัดให้ประเมินเป็นลำดับสุดท้ายใน resolveNextEdge
 */
export function evaluateCondition(
  condition: EdgeCondition,
  def: FlowDefinition,
  answers: AnswerLookup,
): boolean {
  switch (condition.kind) {
    case 'always':
    case 'fallback':
      return true

    case 'optionEquals': {
      const expected = resolveOptionValue(def, condition.nodeKey, condition.fieldName, condition.optionId)
      if (expected === undefined) return false
      const actual = readAnswer(def, answers, condition.nodeKey, condition.fieldName)
      if (typeof actual === 'string') return textEquals(actual, expected, false)
      if (typeof actual === 'number') return String(actual) === expected
      return false
    }

    case 'optionIncludes': {
      const expected = resolveOptionValue(def, condition.nodeKey, condition.fieldName, condition.optionId)
      if (expected === undefined) return false
      const actual = readAnswer(def, answers, condition.nodeKey, condition.fieldName)
      return asStringArray(actual).some((item) => item.trim() === expected.trim())
    }

    case 'fieldAnswered':
      return isAnswered(readAnswer(def, answers, condition.nodeKey, condition.fieldName))

    case 'textEquals': {
      const actual = readAnswer(def, answers, condition.nodeKey, condition.fieldName)
      if (typeof actual === 'string') return textEquals(actual, condition.value, condition.caseInsensitive)
      if (Array.isArray(actual)) {
        return asStringArray(actual).some((item) =>
          textEquals(item, condition.value, condition.caseInsensitive),
        )
      }
      if (typeof actual === 'number') return String(actual) === condition.value.trim()
      return false
    }

    case 'numberCompare': {
      const actual = toNumber(readAnswer(def, answers, condition.nodeKey, condition.fieldName))
      if (actual === null) return false
      switch (condition.op) {
        case 'eq':
          return actual === condition.value
        case 'ne':
          return actual !== condition.value
        case 'lt':
          return actual < condition.value
        case 'lte':
          return actual <= condition.value
        case 'gt':
          return actual > condition.value
        case 'gte':
          return actual >= condition.value
      }
      return false
    }

    case 'expr':
      return evaluateExpr(condition.ast, (ref: FieldRef) =>
        readAnswer(def, answers, ref.nodeKey, ref.fieldName),
      )
  }
}

/** เส้นทางออกของโหนด เรียงตามลำดับการประเมิน (fallback ท้ายสุดเสมอ) */
export function getOrderedOutgoingEdges(def: FlowDefinition, nodeId: string): FlowEdge[] {
  return def.edges
    .filter((edge) => edge.source === nodeId)
    .slice()
    .sort((a, b) => {
      const aFallback = a.condition.kind === 'fallback' ? 1 : 0
      const bFallback = b.condition.kind === 'fallback' ? 1 : 0
      if (aFallback !== bFallback) return aFallback - bFallback
      if (a.priority !== b.priority) return a.priority - b.priority
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    })
}

export type ResolveResult =
  | { type: 'next'; edge: FlowEdge; targetNode: FlowNode }
  | { type: 'finish' }
  /** ไม่มีเส้นทางไหนเข้าเงื่อนไข — ไม่ปล่อยผ่านเงียบ ๆ ต้องแจ้งผู้ใช้และขึ้นใน checklist */
  | { type: 'noMatch'; evaluatedEdgeIds: string[] }
  /** เส้นทางชี้ไปยังโหนดที่ไม่มีอยู่จริง */
  | { type: 'brokenEdge'; edge: FlowEdge }

/**
 * หาเส้นทางถัดไปจากโหนดปัจจุบัน
 * ฟังก์ชันนี้ถูกใช้ทั้งในโหมดจำลองบนกราฟและในเว็บแอปจริง — ผลลัพธ์จึงตรงกันเสมอ
 */
export function resolveNextEdge(
  def: FlowDefinition,
  nodeId: string,
  answers: AnswerLookup,
): ResolveResult {
  const node = def.nodes.find((item) => item.id === nodeId)
  if (!node) return { type: 'noMatch', evaluatedEdgeIds: [] }
  if (node.type === 'end') return { type: 'finish' }

  const outgoing = getOrderedOutgoingEdges(def, nodeId)
  if (outgoing.length === 0) return { type: 'noMatch', evaluatedEdgeIds: [] }

  const evaluated: string[] = []
  for (const edge of outgoing) {
    evaluated.push(edge.id)
    if (!evaluateCondition(edge.condition, def, answers)) continue
    const targetNode = def.nodes.find((item) => item.id === edge.target)
    if (!targetNode) return { type: 'brokenEdge', edge }
    return { type: 'next', edge, targetNode }
  }
  return { type: 'noMatch', evaluatedEdgeIds: evaluated }
}
